/**
 * Pre-TOTP chooser screen (Phase 4).
 *
 * The old flow sent brand-new users straight into TotpSetupScreen
 * with its QR code, authenticator-app references, and secret string —
 * which was a primary drop-off point. This screen sits BEFORE that,
 * explains why TOTP matters in plain language, and gives the user an
 * equal-weight choice: set it up now, or skip for now.
 *
 * Skipping goes through the existing POST /auth/skip-totp-setup
 * endpoint (which decrements the server-side totp_skip_count) so the
 * server still enforces the eventual lockout after the 3rd skip.
 * Proceeding simply forwards to TotpSetupScreen with the same
 * totpSkipsRemaining param it's always received.
 */
import React, { useEffect, useState } from 'react';
import { getAuthToken } from '../../services/auth/tokenStorage';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  SafeAreaView,
  Alert,
  Image,
} from 'react-native';
import { NavigationProp, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ms, s } from '../../utils/responsive';
import { API_URL } from '../../config';
import { createLogger } from '../../utils/logger';
import { analytics, AnalyticsEvent } from '../../services/analytics';

const logger = createLogger('SecurityChooser');

interface Props {
  navigation: NavigationProp<any>;
  route: RouteProp<any, any>;
}

const SecurityChooserScreen: React.FC<Props> = ({ navigation, route }) => {
  const skipsRemainingParam = (route.params as any)?.totpSkipsRemaining ?? 0;
  const [skipping, setSkipping] = useState(false);

  useEffect(() => {
    analytics.track(AnalyticsEvent.SecurityChooserShown, {
      skips_remaining: skipsRemainingParam,
    });
  }, [skipsRemainingParam]);

  const handleProceed = () => {
    analytics.track(AnalyticsEvent.SecurityChooserProceeded);
    navigation.replace('TotpSetup', { totpSkipsRemaining: skipsRemainingParam });
  };

  const handleSkip = async () => {
    if (skipping) return;
    setSkipping(true);
    try {
      let token = await getAuthToken();
      if (!token) token = await getAuthToken();
      if (!token) {
        // No session — fall back to Login. Should not normally happen.
        navigation.reset({
          index: 0,
          routes: [{ name: 'Auth', state: { routes: [{ name: 'Login' }] } }],
        });
        return;
      }

      const response = await fetch(`${API_URL}/auth/skip-totp-setup`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        await AsyncStorage.setItem('@2fa_setup_complete', 'true');
        await AsyncStorage.setItem('@totp_skipped', 'true');
        analytics.track(AnalyticsEvent.SecurityChooserSkipped, {
          skips_remaining: data.skipsRemaining,
        });
        analytics.track(AnalyticsEvent.TotpSetupSkipped, {
          skips_remaining: data.skipsRemaining,
          source: 'security_chooser',
        });
        navigation.replace('Splash');
      } else {
        // If the server refuses (out of skips), drop the user back into
        // full TOTP setup so they see the QR and actually set it up.
        const data = await response.json().catch(() => ({}));
        if (data?.message?.toLowerCase?.().includes('no skip')) {
          Alert.alert(
            'Setup required',
            data.message || 'You need to set up two-step security now.',
            [
              {
                text: 'Continue',
                onPress: () =>
                  navigation.replace('TotpSetup', { totpSkipsRemaining: 0 }),
              },
            ],
          );
        } else {
          Alert.alert('Error', data?.message || 'Could not skip. Please try again.');
        }
      }
    } catch (err) {
      logger.error('Skip failed', err);
      Alert.alert('Error', 'Could not skip. Please check your connection and try again.');
    } finally {
      setSkipping(false);
    }
  };

  const skipsLabel =
    skipsRemainingParam > 0
      ? `Skip for now (${skipsRemainingParam} skip${skipsRemainingParam === 1 ? '' : 's'} left)`
      : 'Skip for now';

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.content}>
        <View style={styles.iconContainer}>
          <Image
            source={require('../../assets/icons/lock.png')}
            style={styles.lockIcon}
          />
        </View>

        <Text style={styles.title}>Extra security for your vault</Text>

        <Text style={styles.body}>
          FinalKey holds information your loved ones will rely on. Adding two-step
          security means a stranger with your password still can't unlock it — and
          neither can anyone impersonating your keyholders.
        </Text>

        <View style={styles.bulletList}>
          <Bullet text="Takes about a minute" />
          <Bullet text="Works with any free authenticator app (Google, Microsoft, Authy)" />
          <Bullet text="You can always set it up later from Settings" />
        </View>

        <View style={styles.buttons}>
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={handleProceed}
            accessibilityLabel="Set up extra security"
          >
            <Text style={styles.primaryButtonText}>Set up extra security</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={handleSkip}
            disabled={skipping}
            accessibilityLabel={skipsLabel}
          >
            {skipping ? (
              <ActivityIndicator color="#1C2C4F" />
            ) : (
              <Text style={styles.secondaryButtonText}>{skipsLabel}</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
};

const Bullet: React.FC<{ text: string }> = ({ text }) => (
  <View style={styles.bulletRow}>
    <Text style={styles.bulletDot}>•</Text>
    <Text style={styles.bulletText}>{text}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  content: {
    flex: 1,
    paddingHorizontal: ms(24),
    paddingTop: ms(40),
    paddingBottom: ms(24),
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: ms(24),
  },
  lockIcon: {
    width: s(64),
    height: s(64),
    tintColor: '#29BFC0',
  },
  title: {
    fontSize: ms(26, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    textAlign: 'center',
    marginBottom: ms(14),
  },
  body: {
    fontSize: ms(15, 0.3),
    color: '#4B5563',
    lineHeight: ms(22, 0.3),
    textAlign: 'center',
    marginBottom: ms(24),
  },
  bulletList: {
    marginBottom: ms(32),
    paddingHorizontal: ms(6),
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: ms(8),
  },
  bulletDot: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginRight: ms(10),
    marginTop: ms(-1),
    lineHeight: ms(22, 0.3),
  },
  bulletText: {
    flex: 1,
    fontSize: ms(14, 0.3),
    color: '#4B5563',
    lineHeight: ms(22, 0.3),
  },
  buttons: {
    marginTop: 'auto',
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(28),
    paddingVertical: ms(16),
    alignItems: 'center',
    marginBottom: ms(10),
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(17, 0.3),
    fontWeight: '700',
  },
  secondaryButton: {
    paddingVertical: ms(14),
    alignItems: 'center',
  },
  secondaryButtonText: {
    color: '#1C2C4F',
    fontSize: ms(15, 0.3),
    fontWeight: '500',
  },
});

export default SecurityChooserScreen;
